import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { accountingApi, formatInr } from "@/api/accounting";
import { useAccountingHospitalContext } from "./AccountingHospitalContext";

type StmtTab = "PL" | "BS" | "CF" | "FA";

function defaultMonthRange() {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, "0");
  const last = new Date(y, now.getMonth() + 1, 0).getDate();
  return {
    from: `${y}-${m}-01`,
    to: `${y}-${m}-${String(last).padStart(2, "0")}`,
    periodKey: `${y}-${m}`,
  };
}

export default function AccountingStatementsPage() {
  const { hospitalId, readOnly } = useAccountingHospitalContext();
  const qc = useQueryClient();
  const defaults = defaultMonthRange();
  const [tab, setTab] = useState<StmtTab>("PL");
  const [fromDate, setFromDate] = useState(defaults.from);
  const [toDate, setToDate] = useState(defaults.to);
  const [asOf, setAsOf] = useState(defaults.to);
  const [periodKey, setPeriodKey] = useState(defaults.periodKey);

  const [faName, setFaName] = useState("");
  const [faCost, setFaCost] = useState("");
  const [faLife, setFaLife] = useState("60");
  const [faCategory, setFaCategory] = useState("EQUIPMENT");
  const [faDate, setFaDate] = useState(defaults.to);
  const [faFunded, setFaFunded] = useState<"BANK" | "CASH" | "AP" | "CAPITAL">("BANK");

  const plQ = useQuery({
    queryKey: ["accounting-pl", hospitalId, fromDate, toDate],
    queryFn: () => accountingApi.profitAndLoss(hospitalId, { fromDate, toDate }),
    enabled: !!hospitalId && tab === "PL" && !!fromDate && !!toDate,
  });

  const bsQ = useQuery({
    queryKey: ["accounting-bs", hospitalId, asOf],
    queryFn: () => accountingApi.balanceSheet(hospitalId, asOf),
    enabled: !!hospitalId && tab === "BS" && !!asOf,
  });

  const cfQ = useQuery({
    queryKey: ["accounting-cf", hospitalId, fromDate, toDate],
    queryFn: () => accountingApi.cashFlow(hospitalId, { fromDate, toDate }),
    enabled: !!hospitalId && tab === "CF" && !!fromDate && !!toDate,
  });

  const faQ = useQuery({
    queryKey: ["accounting-fa", hospitalId],
    queryFn: () => accountingApi.fixedAssetRegister(hospitalId),
    enabled: !!hospitalId && tab === "FA",
  });

  const createFa = useMutation({
    mutationFn: () =>
      accountingApi.createFixedAsset(
        {
          name: faName.trim(),
          category: faCategory,
          purchaseDate: faDate,
          cost: Number(faCost),
          usefulLifeMonths: Number(faLife),
          fundedBy: faFunded,
        },
        hospitalId
      ),
    onSuccess: () => {
      setFaName("");
      setFaCost("");
      qc.invalidateQueries({ queryKey: ["accounting-fa"] });
    },
  });

  const runDep = useMutation({
    mutationFn: () => accountingApi.runDepreciation(periodKey, hospitalId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["accounting-fa"] });
      qc.invalidateQueries({ queryKey: ["accounting-pl"] });
      qc.invalidateQueries({ queryKey: ["accounting-bs"] });
    },
  });

  const tabs: Array<{ id: StmtTab; label: string }> = [
    { id: "PL", label: "P&L" },
    { id: "BS", label: "Balance sheet" },
    { id: "CF", label: "Cash flow" },
    { id: "FA", label: "Fixed assets" },
  ];

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-lg font-medium">Financial statements &amp; fixed assets</h2>
        <p className="mt-1 text-sm text-slate-500">
          Books reports from posted vouchers. Separate from management Hospital P&amp;L / Cash
          Flow until books are proven.
        </p>
      </div>

      <div className="flex flex-wrap gap-1">
        {tabs.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setTab(t.id)}
            className={`rounded-md px-3 py-1.5 text-sm font-medium ${
              tab === t.id ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {(tab === "PL" || tab === "CF") && (
        <div className="flex flex-wrap gap-3">
          <div className="w-44 space-y-1">
            <Label className="text-xs">From</Label>
            <Input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
          </div>
          <div className="w-44 space-y-1">
            <Label className="text-xs">To</Label>
            <Input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} />
          </div>
        </div>
      )}
      {tab === "BS" && (
        <div className="w-44 space-y-1">
          <Label className="text-xs">As of</Label>
          <Input type="date" value={asOf} onChange={(e) => setAsOf(e.target.value)} />
        </div>
      )}

      {tab === "PL" && (
        <div className="space-y-3">
          {plQ.data?.note ? (
            <p className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-600">
              {plQ.data.note}
            </p>
          ) : null}
          <SectionTable title="Revenue" rows={plQ.data?.sections.revenue ?? []} />
          <SectionTable title="Direct costs" rows={plQ.data?.sections.directCosts ?? []} />
          <SectionTable title="Expenses" rows={plQ.data?.sections.expenses ?? []} />
          <div className="grid gap-2 sm:grid-cols-3 text-sm">
            <Stat label="Gross profit" value={plQ.data?.totals.grossProfit} />
            <Stat label="Total expenses" value={plQ.data?.totals.totalExpenses} />
            <Stat label="Net profit" value={plQ.data?.totals.netProfit} emphasize />
          </div>
        </div>
      )}

      {tab === "BS" && (
        <div className="space-y-3">
          {bsQ.data?.note ? (
            <p className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-600">
              {bsQ.data.note}
            </p>
          ) : null}
          <p
            className={`text-sm ${
              bsQ.data?.totals.balanced ? "text-emerald-700" : "text-amber-700"
            }`}
          >
            {bsQ.data?.totals.balanced
              ? "Balance sheet balances (Assets = Liabilities + Equity)."
              : `Out of balance by ${formatInr(bsQ.data?.totals.difference ?? 0)}`}
          </p>
          <SectionTable title="Assets" rows={bsQ.data?.assets ?? []} />
          <SectionTable title="Liabilities" rows={bsQ.data?.liabilities ?? []} />
          <SectionTable title="Equity" rows={bsQ.data?.equity ?? []} />
          <div className="grid gap-2 sm:grid-cols-3 text-sm">
            <Stat label="Total assets" value={bsQ.data?.totals.totalAssets} />
            <Stat label="Liab + Equity" value={bsQ.data?.totals.totalLiabEquity} />
            <Stat label="CY surplus" value={bsQ.data?.totals.currentYearSurplus} />
          </div>
        </div>
      )}

      {tab === "CF" && (
        <div className="space-y-3">
          {cfQ.data?.note ? (
            <p className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-600">
              {cfQ.data.note}
            </p>
          ) : null}
          <p
            className={`text-sm ${
              cfQ.data?.totals.reconciles ? "text-emerald-700" : "text-amber-700"
            }`}
          >
            {cfQ.data?.totals.reconciles
              ? "Cash roll-forward reconciles to closing Cash + Bank."
              : `Cash recon difference ${formatInr(cfQ.data?.totals.reconDifference ?? 0)}`}
          </p>
          <div className="overflow-x-auto rounded-md border border-slate-200">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Line</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(cfQ.data?.lines ?? []).map((l) => (
                  <TableRow key={l.key}>
                    <TableCell className="text-sm">{l.label}</TableCell>
                    <TableCell className="text-right tabular-nums">{formatInr(l.amount)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          <div className="grid gap-2 sm:grid-cols-3 text-sm">
            <Stat label="Opening cash" value={cfQ.data?.totals.openingCash} />
            <Stat label="Net change" value={cfQ.data?.totals.netChangeInCash} />
            <Stat label="Closing cash" value={cfQ.data?.totals.closingCash} emphasize />
          </div>
        </div>
      )}

      {tab === "FA" && (
        <div className="space-y-4">
          {!readOnly ? (
            <div className="flex flex-wrap items-end gap-2 rounded-md border border-slate-200 p-3">
              <div className="w-48 space-y-1">
                <Label className="text-xs">Asset name</Label>
                <Input value={faName} onChange={(e) => setFaName(e.target.value)} />
              </div>
              <div className="w-28 space-y-1">
                <Label className="text-xs">Cost</Label>
                <Input value={faCost} onChange={(e) => setFaCost(e.target.value)} />
              </div>
              <div className="w-24 space-y-1">
                <Label className="text-xs">Life (mo)</Label>
                <Input value={faLife} onChange={(e) => setFaLife(e.target.value)} />
              </div>
              <div className="w-36 space-y-1">
                <Label className="text-xs">Category</Label>
                <Select value={faCategory} onValueChange={setFaCategory}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="EQUIPMENT">Equipment</SelectItem>
                    <SelectItem value="FURNITURE">Furniture</SelectItem>
                    <SelectItem value="VEHICLE">Vehicle</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="w-40 space-y-1">
                <Label className="text-xs">Purchase date</Label>
                <Input type="date" value={faDate} onChange={(e) => setFaDate(e.target.value)} />
              </div>
              <div className="w-32 space-y-1">
                <Label className="text-xs">Funded by</Label>
                <Select
                  value={faFunded}
                  onValueChange={(v) => setFaFunded(v as typeof faFunded)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="BANK">Bank</SelectItem>
                    <SelectItem value="CASH">Cash</SelectItem>
                    <SelectItem value="AP">AP</SelectItem>
                    <SelectItem value="CAPITAL">Capital</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button
                type="button"
                disabled={!faName.trim() || !faCost || createFa.isPending}
                onClick={() => createFa.mutate()}
              >
                Add asset
              </Button>
            </div>
          ) : null}

          <div className="flex flex-wrap items-end gap-2">
            <div className="w-36 space-y-1">
              <Label className="text-xs">Depreciation period</Label>
              <Input
                value={periodKey}
                onChange={(e) => setPeriodKey(e.target.value)}
                placeholder="YYYY-MM"
              />
            </div>
            {!readOnly ? (
              <Button
                type="button"
                variant="outline"
                disabled={!periodKey || runDep.isPending}
                onClick={() => runDep.mutate()}
              >
                Run depreciation
              </Button>
            ) : null}
            {runDep.data ? (
              <span className="text-sm text-slate-600">
                Posted {runDep.data.postedCount} · {formatInr(runDep.data.totalDepreciation)}
              </span>
            ) : null}
          </div>

          <div className="overflow-x-auto rounded-md border border-slate-200">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Asset</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead className="text-right">Cost</TableHead>
                  <TableHead className="text-right">Accum dep</TableHead>
                  <TableHead className="text-right">NBV</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(faQ.data?.rows ?? []).map((r) => (
                  <TableRow key={r.id}>
                    <TableCell className="text-sm font-medium">{r.name}</TableCell>
                    <TableCell className="text-sm">{r.category}</TableCell>
                    <TableCell className="text-right tabular-nums">{formatInr(r.cost)}</TableCell>
                    <TableCell className="text-right tabular-nums">{formatInr(r.accumDep)}</TableCell>
                    <TableCell className="text-right tabular-nums">
                      {formatInr(r.netBookValue)}
                    </TableCell>
                    <TableCell className="text-sm">{r.status}</TableCell>
                  </TableRow>
                ))}
                {!faQ.isLoading && !(faQ.data?.rows ?? []).length ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-sm text-slate-500">
                      No fixed assets yet.
                    </TableCell>
                  </TableRow>
                ) : null}
              </TableBody>
            </Table>
          </div>
          {faQ.data?.totals ? (
            <div className="grid gap-2 sm:grid-cols-3 text-sm">
              <Stat label="Total cost" value={faQ.data.totals.cost} />
              <Stat label="Accum dep" value={faQ.data.totals.accumDep} />
              <Stat label="Net book value" value={faQ.data.totals.netBookValue} emphasize />
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
}

function Stat({
  label,
  value,
  emphasize,
}: {
  label: string;
  value?: number;
  emphasize?: boolean;
}) {
  return (
    <div className="rounded-md border border-slate-200 px-3 py-2">
      <div className="text-xs text-slate-500">{label}</div>
      <div className={`tabular-nums ${emphasize ? "text-lg font-medium" : "text-base"}`}>
        {formatInr(value ?? 0)}
      </div>
    </div>
  );
}

function SectionTable({
  title,
  rows,
}: {
  title: string;
  rows: Array<{ code: string; name: string; balance: number }>;
}) {
  return (
    <div>
      <h3 className="mb-1 text-sm font-medium text-slate-800">{title}</h3>
      <div className="overflow-x-auto rounded-md border border-slate-200">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Code</TableHead>
              <TableHead>Account</TableHead>
              <TableHead className="text-right">Amount</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((r) => (
              <TableRow key={r.code}>
                <TableCell className="font-mono text-xs">{r.code}</TableCell>
                <TableCell className="text-sm">{r.name}</TableCell>
                <TableCell className="text-right tabular-nums">{formatInr(r.balance)}</TableCell>
              </TableRow>
            ))}
            {!rows.length ? (
              <TableRow>
                <TableCell colSpan={3} className="text-sm text-slate-500">
                  No lines
                </TableCell>
              </TableRow>
            ) : null}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
